 
import {
  createSms,
  patchSmsById,
  updateSmsRecipients,
  sendSms as sendSmiApi,
  deleteSms,
  getSmsReciept,
  getSmsSettings,
  getSmsStatus,
} from 'Modules/Communication/Sms/shared/api/sms.api'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { addBoardContact } from 'shared/api/client.api'
import { getActiveClient } from 'shared/store/clients/selectors'
import {
  SEND_SMS_REQUESTED,
  SEND_SMS_SUCCESS,
  SEND_SMS_ERROR,
  SEND_SMS_UPDATE_DRAFT_REQUESTED,
  SEND_SMS_CREATE_DRAFT_REQUESTED,
  SEND_SMS_CREATE_DRAFT_ERROR,
  SEND_SMS_CREATE_DRAFT_SUCCESS,
  SEND_SMS_UPDATE_DRAFT_ERROR,
  SEND_SMS_UPDATE_DRAFT_SUCCESS,
  SEND_SMS_DELETE_DRAFT_REQUESTED,
  SEND_SMS_DELETE_DRAFT_SUCCESS,
  SEND_SMS_DELETE_DRAFT_ERROR,
  SEND_SMS_UPDATE_RECIPIENTS_REQUESTED,
  SEND_SMS_CREATE_CONTACT_REQUESTED,
  SEND_SMS_CREATE_CONTACT_FAILED,
  SEND_SMS_CREATE_CONTACT_SUCCESS,
  SEND_SMS_REMOVE_RECIPIENT_REQUESTED,
  SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED,
  SEND_SMS_FETCH_RECIEPT_REQUESTED,
  SEND_SMS_FETCH_RECIEPT_ERROR,
  SEND_SMS_FETCH_RECIEPT_SUCCESS,
  SEND_SMS_FETCH_SETTINGS_REQUESTED,
  SEND_SMS_FETCH_SETTINGS_SUCCESS,
  SEND_SMS_FETCH_SETTINGS_ERROR,
  SEND_SMS_UPDATE_SENDING_STATUS_REQUESTED,
  SEND_SMS_UPDATE_SENDING_STATUS_ERROR,
  SEND_SMS_UPDATE_SENDING_STATUS_SUCCESS,
  SEND_SMS_UPDATE_RECIPIENTS_SUCCESS,
  SEND_SMS_UPDATE_MANY_RECIPIENTS_SUCCESS,
  SEND_SMS_REMOVE_RECIPIENT_SUCCESS,
  SEND_SMS_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
  SEND_SMS_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
  SEND_SMS_REFETCH_SENDING_STATUS_REQUESTED,
  SEND_SMS_REFETCH_SENDING_STATUS_SUCCESS,
} from './actionTypes'
import {
  getSendSms,
  getSendSmsRecipients,
  getSmsReciept as recieptSelector,
} from './selectors'
import { SendSmsActionTypes } from './types'
import {
  IMessageRecipient,
  InboxSectionEnum,
} from 'Modules/Communication/shared/models/Inbox'
import { IPatchModel } from 'shared/models/Common'
import { ISendSmsState } from '../shared/SendMessage'
import {
  SMS_FETCH_NEW_SILENTLY_REQUESTED,
  SMS_UPDATE_DRAFT,
} from 'shared/store/communication/smsMessages/actionTypes'
import { ILoadingMessageRecipient } from 'shared/models/Message'
import { nanoid } from '@reduxjs/toolkit'
import { addToasterMessage } from 'shared/store/toaster'

function* fetchSmsSettings() {
  try {
    const { id } = yield select(getActiveClient)

    const [{ data: settingsData }, { data: statusData }] = yield all([
      call(getSmsSettings, id),
      call(getSmsStatus, id),
    ])
    yield put({
      type: SEND_SMS_FETCH_SETTINGS_SUCCESS,
      payload: { ...settingsData, ...statusData },
    })
  } catch {
    yield put({
      type: SEND_SMS_FETCH_SETTINGS_ERROR,
      payload: 'Det skjedde en feil ved uthenting av innstillinger for SMS',
    })
  }
}

function* sendSms(action: SendSmsActionTypes) {
  try {
    const { message }: ISendSmsState = yield select(getSendSms)
    const messageFromForm: string = action.payload.message
    const id: string = action.payload.messageId

    // Validate draft is updated
    if (message?.content !== messageFromForm)
      yield call(patchSmsById, id, [
        {
          op: 'replace',
          path: '/content',
          value: messageFromForm,
        },
      ])

    yield call(sendSmiApi, id)

    yield put({ type: SEND_SMS_SUCCESS })
    yield put({ type: SEND_SMS_FETCH_RECIEPT_REQUESTED, payload: id })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_ERROR,
      payload:
        e.response.data?.Message || 'Det skjedde en feil ved sending av e-post',
    })
  }
}

function* createDraft(action: SendSmsActionTypes) {
  try {
    const { id } = yield select(getActiveClient)

    const { data } = yield call(createSms, id, action.payload)

    yield put({
      type: SEND_SMS_CREATE_DRAFT_SUCCESS,
      payload: { id: data, message: { content: action.payload } },
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_CREATE_DRAFT_ERROR,
      payload:
        e.response.data?.Message ||
        'Det skjedde en feil ved opprettelse av utkast',
    })
  }
}

function* updateDraft(action: SendSmsActionTypes) {
  try {
    yield call(patchSmsById, action.payload.id, [
      {
        op: 'replace',
        path: '/content',
        value: action.payload.value,
      },
    ])

    yield put({
      type: SEND_SMS_UPDATE_DRAFT_SUCCESS,
      payload: action.payload.value,
    })
    yield put({
      type: SMS_UPDATE_DRAFT,
      payload: action.payload,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_UPDATE_DRAFT_ERROR,
      payload:
        e.response?.data?.Message ||
        'Det skjedde en feil ved oppdatering av utkast',
    })
  }
}

function* updateRecipient(action: SendSmsActionTypes) {
  const payload = action.payload as IMessageRecipient
  const loadingItem: ILoadingMessageRecipient[] = [
    {
      // TODO: update to item.id
      id: payload.nameregisterId || nanoid(),
      type: 'add',
      recipientType: payload.recipientType,
    },
  ]

  yield put({
    type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
    payload: loadingItem,
  })

  try {
    const { messageId }: { messageId: string } = yield select(getSendSms)
    const recipients: IMessageRecipient[] = yield select(getSendSmsRecipients)
    const isAlreadyRegistered = recipients.some(
      (recipient: IMessageRecipient) =>
        recipient.phonenumber?.toLowerCase() ===
        payload.phonenumber?.toLowerCase()
    )

    if (payload.phonenumber && isAlreadyRegistered) return

    // If in draft mode
    if (messageId) {
      const updateObj = {
        op: 'add',
        path: '/recipients',
        value: [payload],
      } as IPatchModel
      yield call(updateSmsRecipients, messageId, [updateObj])
    }
    yield put({
      type: SEND_SMS_UPDATE_RECIPIENTS_SUCCESS,
      payload,
    })
  } catch (e: any) {
    console.log(e.response?.data)
  } finally {
    yield put({
      type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItem,
    })
  }
}

function* deleteDraft(action: SendSmsActionTypes) {
  try {
    yield call(deleteSms, action.payload)

    yield put({
      type: SEND_SMS_DELETE_DRAFT_SUCCESS,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_DELETE_DRAFT_ERROR,
      payload:
        e.response.data?.Message ||
        'Det skjedde en feil ved sletting av utkast',
    })
  }
}

function* fetchReciept(action: SendSmsActionTypes) {
  try {
    const { data } = yield call(getSmsReciept, action.payload)
    yield put({
      type: SEND_SMS_FETCH_RECIEPT_SUCCESS,
      payload: data,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_FETCH_RECIEPT_ERROR,
      payload: 'Det skjedde en feil ved uthenting av kvittering',
    })
  }
}

/**
 * Will handle signalR message from backend. if Status = success - sets sendingstatus = sent.
 * If error in sending status, display error message
 */
function* handleSendingStatus(action: SendSmsActionTypes) {
  const { messageId: activeRecieptMessageId } = yield select(recieptSelector)
  const messageId = action.payload?.Notification?.Payload?.MessageId
  const sendingStatusPayload = action.payload?.Notification?.Payload
  const isActiveReciept = Boolean(
    messageId && messageId === activeRecieptMessageId
  )
  try {
    if (sendingStatusPayload?.Success) {
      yield put({
        type: SMS_FETCH_NEW_SILENTLY_REQUESTED,
        payload: { section: InboxSectionEnum.Sent },
      })
    }
    if (isActiveReciept && sendingStatusPayload?.Success) {
      yield put({
        type: SEND_SMS_UPDATE_SENDING_STATUS_SUCCESS,
      })
    } else if (
      isActiveReciept &&
      sendingStatusPayload &&
      !sendingStatusPayload.Success
    ) {
      yield put({
        type: SEND_SMS_UPDATE_SENDING_STATUS_ERROR,
        payload: 'SMS-en ble ikke sendt. Vennligst prøv på ny.',
      })

      yield put(
        addToasterMessage({
          text: 'SMS-en ble ikke sendt. Vennligst prøv på ny',
          type: 'danger',
        })
      )
    }
  } catch (e: any) {
    console.log(e.response?.data)
  }
}

function* createContact(action: SendSmsActionTypes) {
  try {
    const { id } = yield select(getActiveClient)

    yield call(addBoardContact, id, action.payload)
    yield put({
      type: SEND_SMS_CREATE_CONTACT_SUCCESS,
      payload: action.payload,
    })
  } catch (e: any) {
    console.log(e.response?.data)
    yield put({
      type: SEND_SMS_CREATE_CONTACT_FAILED,
      payload: 'Det skjedde en feil ved oppretting av kontakt',
    })
  }
}

function* removeRecipient(action: SendSmsActionTypes) {
  const payload = action.payload as IMessageRecipient

  const loadingItems = [
    {
      // TODO: update to item.id
      id: payload.nameregisterId || payload.email || nanoid(),
      type: 'remove',
      recipientType: payload.recipientType,
    },
  ]

  yield put({
    type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
    payload: loadingItems,
  })

  try {
    const { messageId }: { messageId: string } = yield select(getSendSms)

    // If in draft mode
    if (messageId) {
      const updateObj = {
        op: 'remove',
        path: '/recipients',
        value: [
          {
            nameregisterId: payload.nameregisterId,
            phonenumber: payload.phonenumber,
          },
        ],
      } as IPatchModel
      yield call(updateSmsRecipients, messageId, [updateObj])
    }
    yield put({
      type: SEND_SMS_REMOVE_RECIPIENT_SUCCESS,
      payload: {
        nameregisterId: payload.nameregisterId,
        phonenumber: payload.phonenumber ?? undefined,
      },
    })
  } catch (e: any) {
    console.log(e.response?.data)
  } finally {
    yield put({
      type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItems,
    })
  }
}

function* refetchSendingStatus(action: SendSmsActionTypes) {
  try {
    const { data } = yield call(getSmsReciept, action.payload)

    yield put({
      type: SEND_SMS_REFETCH_SENDING_STATUS_SUCCESS,
      payload: data.status,
    })
  } catch (e: any) {
    console.log(e.response?.data)
  }
}

function* updateManyRecipients(action: SendSmsActionTypes) {
  let loadingItems = [] as ILoadingMessageRecipient[]

  try {
    const payload = action.payload as IMessageRecipient[]
    const { messageId }: { messageId: string } = yield select(getSendSms)
    const recipients: IMessageRecipient[] = yield select(getSendSmsRecipients)
    // check if recipients exists in payload
    const payloadRecipientsThatIsNotAdded = payload.filter(
      (x) =>
        !recipients.find(
          (y) =>
            (y.phonenumber?.toLowerCase() === x.phonenumber?.toLowerCase() &&
              x.nameregisterId === y.nameregisterId) ||
            // Specific for phonenumbers the user adds themselves
            (y.phonenumber === x.phonenumber && x.phonenumber !== null)
        )
    )
    if (!payloadRecipientsThatIsNotAdded.length) return
    // If in draft mode

    loadingItems = payloadRecipientsThatIsNotAdded.map(
      (item) =>
        ({
          // TODO: update to item.id
          id: item.nameregisterId || nanoid(),
          type: 'add',
          recipientType: item.recipientType,
        } as ILoadingMessageRecipient)
    )

    yield put({
      type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_REQUESTED,
      payload: loadingItems,
    })

    if (messageId) {
      const updateObj = {
        op: 'add',
        path: '/recipients',
        value: payloadRecipientsThatIsNotAdded,
      } as IPatchModel
      yield call(updateSmsRecipients, messageId, [updateObj])
    }
    yield put({
      type: SEND_SMS_UPDATE_MANY_RECIPIENTS_SUCCESS,
      payload: payloadRecipientsThatIsNotAdded,
    })
  } catch (e: any) {
    console.log(e)
  } finally {
    yield put({
      type: SEND_SMS_RECIPIENTS_TO_LOADING_LIST_SUCCESS,
      payload: loadingItems,
    })
  }
}

function* sendSmsSaga() {
  yield takeEvery(SEND_SMS_FETCH_SETTINGS_REQUESTED, fetchSmsSettings)
  yield takeEvery(SEND_SMS_REQUESTED, sendSms)
  yield takeEvery(SEND_SMS_CREATE_DRAFT_REQUESTED, createDraft)
  yield takeEvery(SEND_SMS_UPDATE_DRAFT_REQUESTED, updateDraft)
  yield takeEvery(SEND_SMS_DELETE_DRAFT_REQUESTED, deleteDraft)
  yield takeEvery(SEND_SMS_UPDATE_RECIPIENTS_REQUESTED, updateRecipient)
  yield takeEvery(
    SEND_SMS_UPDATE_MANY_RECIPIENTS_REQUESTED,
    updateManyRecipients
  )
  yield takeEvery(SEND_SMS_FETCH_RECIEPT_REQUESTED, fetchReciept)
  yield takeEvery(SEND_SMS_CREATE_CONTACT_REQUESTED, createContact)
  yield takeEvery(SEND_SMS_REMOVE_RECIPIENT_REQUESTED, removeRecipient)
  yield takeEvery(SEND_SMS_UPDATE_SENDING_STATUS_REQUESTED, handleSendingStatus)
  yield takeEvery(
    SEND_SMS_REFETCH_SENDING_STATUS_REQUESTED,
    refetchSendingStatus
  )
}

export default sendSmsSaga
